import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import { getToken } from "../../utils/GetToken";
import { GetCompaniesListService } from "../../services/Companies/GetCompaniesListService";
import assignDataGridLanguage from "../../utils/AssignDatagridLanguage";
import dayjs from "dayjs";
import { HeadersCompanies } from "../../utils/TableHeaders/CompaniesHeaders";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";

export default function CompaniesList() {
  const { t } = useTranslation();
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [languageDatagrid, setLanguageDatagrid] = useState({});
  const [queryOptions, setQueryOptions] = useState({});
  const [query, setQuery] = useState(searchParams ? searchParams : {});
  const pageUrl = searchParams.get("page");
  const paginationUrl = searchParams.get("pagination");
  const fromParamUrl = searchParams.get("from");
  const toParamUrl = searchParams.get("to");
  const { isCompanyDeleted, resetCompanyState } = useCompany();

  const [pageFromUrl, setPageFromUrl] = useState(
    pageUrl !== null && pageUrl !== 0
      ? parseInt(searchParams.get("page"), 10)
      : 0
  );
  const [paginationFromUrl, setPaginationFromUrl] = useState(
    paginationUrl !== null && paginationUrl !== 0
      ? parseInt(searchParams.get("pagination"), 10)
      : 100
  );
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: pageFromUrl,
    pageSize: paginationFromUrl,
  });
  const [paginationModel, setPaginationModel] = useState({
    page: pageFromUrl,
    pageSize: paginationFromUrl,
  });
  const currentLanguage = localStorage.getItem("i18nextLng");
  const [dateFrom, setDateFrom] = useState(
    fromParamUrl !== null ? dayjs(searchParams.get("from")) : null
  );
  const [dateTo, setDateTo] = useState(
    toParamUrl !== null ? dayjs(searchParams.get("to")) : null
  );
  const currentURL = window.location.href;

  const handlePageChange = (params) => {
    setPageState((old) => ({
      ...old,
      page: params.page,
      pageSize: params.pageSize,
    }));
    setPaginationModel((old) => ({
      ...old,
      page: params.page,
      pageSize: params.pageSize,
    }));
  };

  const extractParamsAndAssignState = () => {
    const currentURL = window.location.href;
    const urlSearchParams = new URLSearchParams(currentURL.split("?")[1]);
    const paramsArray = [];

    urlSearchParams.forEach((value, key) => {
      paramsArray.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    });

    const state = paramsArray.join("&");
    setQuery(state);

    return state;
  };

  useEffect(() => {
    setLanguageDatagrid(assignDataGridLanguage(currentLanguage));
    extractParamsAndAssignState();
  }, [currentLanguage]);

  const onFilterChange = useCallback(
    (filterModel) => {
      setQueryOptions({ filterModel: { ...filterModel } });
    },
    [query]
  );

  useEffect(() => {
    setPaginationModel((old) => ({
      ...old,
      page: pageFromUrl,
      pageSize: paginationFromUrl,
    }));
  }, [pageFromUrl, paginationFromUrl, query]);

  useEffect(() => {
    searchParams.set("page", `${pageState.page}`);
    searchParams.set("pagination", `${pageState.pageSize}`);
    setSearchParams(searchParams);
  }, [pageState.page, pageState.pageSize]);

  useEffect(() => {
    setPageState((old) => ({
      ...old,
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
    }));
    getCompanies();
  }, [pageState.page, pageState.pageSize, query, paginationModel]);

  useEffect(() => {
    if (isCompanyDeleted) {
      getCompanies();
      setShowDeleteMessage(true);
      resetCompanyState();
    }
  }, [isCompanyDeleted]);

  useEffect(() => {
    const formattedDateFrom = dayjs(dateFrom).format("YYYY-MM-DD");
    // console.log("inside date from");
    if (formattedDateFrom !== null && dayjs(formattedDateFrom).isValid()) {
      searchParams.set(`from`, `${formattedDateFrom}`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    } else {
      searchParams.delete(`from`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    }
  }, [dateFrom]);

  useEffect(() => {
    const formattedDateTo = dayjs(dateTo).format("YYYY-MM-DD");

    if (formattedDateTo !== null && dayjs(formattedDateTo).isValid()) {
      searchParams.set(`to`, `${formattedDateTo}`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    } else {
      searchParams.delete(`to`);
      setSearchParams(searchParams);
      extractParamsAndAssignState();
    }
  }, [dateTo]);

  const getCompanies = async () => {
    try {
      const token = await getToken();
      if (token) {
        setPageState((old) => ({ ...old, isLoading: true }));
        const currentPage = pageState.page + 1;
        const pagination = pageState.pageSize;
        const response = await GetCompaniesListService(
          token,
          currentPage,
          query,
          pagination
        );
        const { data, links, meta } = response;
        // console.log(data);
        if (Array.isArray(data) && data.length !== 0) {
          const mappedData = mapData(data);
          setPageState((old) => ({
            ...old,
            isLoading: false,
            data: mappedData,
            total: meta.total,
          }));
        } else {
          setPageState((old) => ({
            ...old,
            isLoading: false,
            data: [],
            total: 0,
          }));
        }
        return;
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const mapData = (data) =>
    data.map((item) => {
      const mappedItem = {
        id: item?.id,
        name: item?.name,
        type_name: item?.type.name,
        type_active: item?.type.active,
        type_order: item?.type.order,
        description: item?.description,
        createdAt: dayjs(item?.createdAt).format("DD/MM/YYYY HH:mm"),
        lastUpdate: dayjs(item?.updatedAt).format("DD/MM/YYYY HH:mm"),
      };
      return mappedItem;
    });

  const resetPagination = () => {
    searchParams.set("page", 0);
    searchParams.set("pagination", 100);
    setPageFromUrl(parseInt(searchParams.get("page"), 10));
    setPaginationFromUrl(parseInt(searchParams.get("pagination"), 10));
  };

  const handleCloseDeleteCompaniesMessage = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setShowDeleteMessage(false);
  };

  return (
    <>
      <DataGrid
        rows={pageState.data}
        columns={HeadersCompanies()}
        localeText={languageDatagrid}
        rowCount={pageState.total}
        loading={pageState.isLoading}
        pagination
        page={pageState.page}
        filterMode="server"
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={(params) => handlePageChange(params)}
        onFilterModelChange={onFilterChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        slots={{
          toolbar: GridToolbar,
        }}
        sx={{
          boxShadow: "0 1px 12px rgba(149, 157, 165, 0.2)",
          borderRadius: "25px",
          borderColor: "transparent",
          backgroundColor: "white",
          paddingX: "1.5rem",
          "& .MuiDataGrid-cell": {
            borderColor: "gray.light",
          },
          "& .MuiDataGrid-cell:hover": {
            color: "secondary.main",
          },
          "& .super-app-theme--header": {
            backgroundColor: "white",
          },
        }}
      />
      <Snackbar
        open={showDeleteMessage}
        autoHideDuration={3000}
        onClose={handleCloseDeleteCompaniesMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success">{t("companies.delete.success.message")}</Alert>
      </Snackbar>
    </>
  );
}
